import { faIndent } from '@fortawesome/free-solid-svg-icons';
import CodeEditor from '@uiw/react-textarea-code-editor';
import Ajv, { AnySchemaObject, ErrorObject, ValidateFunction } from 'ajv';
import addFormats from 'ajv-formats';
import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Alert, Button } from '.';
import theme from '../exports.module.scss';
import classNames from '../utilities/class-names';
import './JsonEditor.scss';

type JsonEditorProps = {
  className?: string;
  id?: string;
  name?: string;
  value?: any;
  disabled?: boolean;
  allowEmpty?: boolean;
  schema?: AnySchemaObject;
  /**
   * Called when the user edits the text
   *
   * If the text can't be parsed, value will be undefined and error will be set
   * If the text parses but fails schema validation, both value and error will
   * be set
   */
  onChange?: (value: any, error?: string) => void;
  [key: string]: any;
};

type EvaluateResult = {
  value: any;
  error: string | null;
  errorType: 'parse' | 'validate' | null;
};

const stringify = (json: any): string =>
  json === undefined ? '' : JSON.stringify(json, null, 2);

function evaluate(
  text: string,
  allowEmpty: boolean,
  validator?: ValidateFunction
): EvaluateResult {
  if (allowEmpty && text.trim() === '') {
    return { value: null, error: null, errorType: null };
  }

  let value: any;
  try {
    value = JSON.parse(text);
  } catch (e) {
    return {
      value: undefined,
      error: (e as Error).message,
      errorType: 'parse',
    };
  }

  if (validator && !validator(value) && validator.errors) {
    return {
      value,
      error: validator.errors
        .map(
          (e: ErrorObject) =>
            `Schema validation error: ${e.message} ${
              e.instancePath ? `at ${e.instancePath}` : ''
            }`
        )
        .join('\n'),
      errorType: 'validate',
    };
  }

  return { value, error: null, errorType: null };
}

export function JsonEditor({
  className,
  id,
  name,
  value,
  disabled,
  allowEmpty,
  onChange,
  schema,
  ...props
}: JsonEditorProps): ReactElement {
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(() => stringify(value));

  // The serialised value that the current text corresponds to, either because
  // we emitted it or because it was passed in; used to tell a value that has
  // round-tripped through the parent apart from a genuine external change
  const syncedValue = useRef<string | undefined>(JSON.stringify(value));

  // Callers often pass schema objects inline, so key on content not identity
  const schemaKey = schema ? JSON.stringify(schema) : undefined;
  const validator = useMemo(() => {
    if (!schema) {
      return undefined;
    }

    try {
      const ajv = new Ajv();
      addFormats(ajv);

      return ajv.compile(schema);
    } catch (e) {
      console.error('JsonEditor: unable to compile schema', e);
      return undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schemaKey]);

  const result = useMemo(
    () => evaluate(text, !!allowEmpty, validator),
    [text, allowEmpty, validator]
  );
  const canFormat = result.errorType !== 'parse' && text.trim() !== '';

  useEffect(() => {
    const serialised = JSON.stringify(value);
    if (serialised === syncedValue.current) {
      return;
    }

    syncedValue.current = serialised;
    setText(stringify(value));
  }, [value]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newText = e.target.value;
      setText(newText);

      const newResult = evaluate(newText, !!allowEmpty, validator);
      if (newResult.errorType !== 'parse') {
        syncedValue.current = JSON.stringify(newResult.value);
      }

      onChange?.(newResult.value, newResult.error ?? undefined);
    },
    [allowEmpty, validator, onChange]
  );

  const format = useCallback(() => {
    if (!canFormat) {
      return;
    }

    setText(stringify(result.value));
  }, [canFormat, result.value]);

  return (
    <>
      <div
        className={classNames(
          'json-editor',
          className,
          disabled ? 'json-editor-disabled' : '',
          focused ? 'json-editor-focussed' : ''
        )}
        {...props}
      >
        <CodeEditor
          id={id ?? name}
          name={name}
          value={text}
          language="json"
          placeholder={'Edit JSON here...'}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={handleChange}
          padding={12}
          disabled={disabled}
          style={{
            width: '100%',
            backgroundColor: theme.brandBg1,
            lineHeight: '1.5em',
            fontSize: '1em',
            fontFamily: theme.codeFontStack,
          }}
        />
        <Button
          className="json-editor-format-button"
          icon={faIndent}
          disabled={disabled || !canFormat}
          onClick={format}
          minimal
        />
      </div>
      <Alert
        intent={result.errorType === 'parse' ? 'danger' : 'warning'}
        show={!!result.error}
      >
        <p>{result.error}</p>
      </Alert>
    </>
  );
}
